/**
 * Data layer for `allowed-traffic`. Thin typed fetch wrappers over
 * /api/v1/allowed-traffic. The API models a per-campaign traffic-source
 * whitelist: each campaign_id maps to an array of canonical channel codes.
 *
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export type ChannelCode =
	| "display"
	| "email"
	| "incentive"
	| "influencer"
	| "native"
	| "push"
	| "search"
	| "seo"
	| "social"
	| "video";

export interface AllowedTrafficView {
	campaign_id: string;
	channels: ChannelCode[];
	created_at: number;
	updated_at: number;
}

export interface ListAllowedTrafficResponse {
	campaigns: AllowedTrafficView[];
	allowed_channels: ChannelCode[];
}

export interface CampaignAllowedTrafficResponse {
	campaign_id: string;
	whitelist: AllowedTrafficView | null;
	allowed_channels: ChannelCode[];
}

export interface UpsertCampaignBody {
	channels: ChannelCode[];
}

export interface UpsertCampaignResponse {
	whitelist: AllowedTrafficView;
}

const BASE = "/api/v1/allowed-traffic";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAllowedTrafficResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAllowedTrafficResponse>(res);
}

export async function getCampaign(
	campaignId: string,
): Promise<CampaignAllowedTrafficResponse> {
	const res = await fetch(
		`${BASE}/campaign/${encodeURIComponent(campaignId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<CampaignAllowedTrafficResponse>(res);
}

export async function upsertCampaign(
	campaignId: string,
	body: UpsertCampaignBody,
): Promise<UpsertCampaignResponse> {
	const res = await fetch(
		`${BASE}/campaign/${encodeURIComponent(campaignId)}`,
		{
			method: "PUT",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify(body),
		},
	);
	return parseOrThrow<UpsertCampaignResponse>(res);
}

export const allowedTrafficQueryKeys = {
	all: ["allowed-traffic"] as const,
	list: () => [...allowedTrafficQueryKeys.all] as const,
	detail: (campaignId: string) =>
		[...allowedTrafficQueryKeys.all, campaignId] as const,
};
